import { useState } from "react";

/**
 * Поле ввода пароля с кнопкой-«глазом» (D-1). Вынесено из страницы входа в общий компонент,
 * когда появилась вторая форма с паролем — экран смены пароля (D-15): D-1 прямо оговаривал,
 * что будущие такие формы переиспользуют паттерн, а не повторяют его разметку.
 *
 * `autoComplete` задаёт вызывающий (D-16б): на входе — `current-password`, на смене нового
 * пароля — `new-password`, иначе менеджеры паролей предлагают сохранить не то поле.
 * Пароль не логируется нигде.
 */
export interface PasswordFieldProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly autoComplete: "current-password" | "new-password";
  readonly disabled?: boolean;
  readonly autoFocus?: boolean;
  /** Пояснение под полем (например, требования политики) — читается диктором вместе с полем. */
  readonly hint?: string;
}

/** Иконка «глаз» (показать) / «глаз перечёркнут» (скрыть). Inline-SVG — без внешних зависимостей. */
function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
      {off && <line x1="3" y1="3" x2="21" y2="21" />}
    </svg>
  );
}

export function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  disabled = false,
  autoFocus = false,
  hint,
}: PasswordFieldProps) {
  const [shown, setShown] = useState(false);
  const hintId = hint ? `${id}-hint` : undefined;

  return (
    <div>
      <label htmlFor={id} className="block text-[13px] font-bold text-ink">
        {label}
      </label>
      <div className="relative mt-1">
        <input
          id={id}
          name={id}
          type={shown ? "text" : "password"}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          required
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          aria-describedby={hintId}
          className="w-full rounded-[var(--radius-m)] border border-border bg-surface px-3 py-2 pr-10 text-[15px] text-ink disabled:opacity-60"
        />
        <button
          type="button"
          onClick={() => setShown((v) => !v)}
          disabled={disabled}
          aria-pressed={shown}
          aria-controls={id}
          aria-label={shown ? "Скрыть пароль" : "Показать пароль"}
          title={shown ? "Скрыть пароль" : "Показать пароль"}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-muted hover:text-ink disabled:opacity-60"
        >
          <EyeIcon off={shown} />
        </button>
      </div>
      {hint && (
        <p id={hintId} className="mt-1 text-[12px] text-muted">
          {hint}
        </p>
      )}
    </div>
  );
}
